import React from 'react';
import './Table.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';

/**
 * A reusable Table component
 *
 * @param {Object} props
 * @param {Array} props.columns - Column defs: { key, title, sortable, sortKey (optional; used for sort state vs col.key), render }
 * @param {Array} props.data - Array of data objects to render
 * @param {string} props.sortBy - Currently sorted column key
 * @param {string} props.sortOrder - 'asc' or 'desc'
 * @param {Function} props.onSort - Function(columnKey) called when a sortable header is clicked
 * @param {boolean} props.isLoading - Whether the data is loading
 * @param {'spinner'|'skeleton'} props.loadingMode - How to show loading row(s)
 * @param {number} props.skeletonRows - Row count when loadingMode is skeleton
 * @param {string} props.emptyMessage - Message to display when no data
 * @param {Function} props.onRowClick - (row, rowIndex) => void when a data row is clicked
 * @param {Function} props.getRowKey - (row, rowIndex) => unique key; defaults to row.id or index
 */
const Table = ({
  columns = [],
  data = [],
  sortBy,
  sortOrder,
  onSort,
  isLoading = false,
  loadingMode = 'spinner',
  skeletonRows = 8,
  emptyMessage = 'No records found',
  onRowClick,
  getRowKey
}) => {
  // Memoize column headers
  const tableHeaders = React.useMemo(() => {
    return columns.map((col, index) => {
      const sortable = !!col.sortable;
      const sortColumnKey = col.sortKey ?? col.key;
      const centered = (col.className || '').includes('reusable-table-centered-col');
      const active = sortable && sortBy === sortColumnKey;
      const spreadSortIcon = sortable && centered;

      return (
        <th
          key={col.key || index}
          className={[sortable ? 'reusable-table-sortable-header' : '', col.className || ''].join(' ').trim()}
          onClick={() => sortable && onSort && onSort(sortColumnKey)}
          style={col.style}
        >
          <span className={`reusable-table-th-content${spreadSortIcon ? ' reusable-table-th-content--spread' : ''}`}>
            <span className="reusable-table-th-label">{col.title}</span>
            {sortable && active && (
              <FontAwesomeIcon
                icon={sortOrder === 'asc' ? faSortUp : faSortDown}
                className="reusable-table-sort-icon"
              />
            )}
          </span>
        </th>
      );
    });
  }, [columns, sortBy, sortOrder, onSort]);

  // Memoize table body rows
  const tableBody = React.useMemo(() => {
    const hasData = data.length > 0;

    // Show skeleton/spinner if we are loading
    if (isLoading) {
      if (loadingMode === 'skeleton') {
        return Array.from({ length: skeletonRows }, (_, ri) => (
          <tr key={`sk-${ri}`} className="reusable-table-skeleton-row">
            {columns.map((col, ci) => (
              <td key={ci}>
                <div className="reusable-table-skeleton-cell" />
              </td>
            ))}
          </tr>
        ));
      }

      // If we have data and use spinner mode, we might want to keep showing data but dimmed
      if (!hasData) {
        return (
          <tr>
            <td colSpan={columns.length} className="reusable-table-loading-cell">
              <div className="d-flex justify-content-center align-items-center py-3">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            </td>
          </tr>
        );
      }
    }

    if (!hasData) {
      return (
        <tr>
          <td colSpan={columns.length} className="reusable-table-empty-cell">
            {emptyMessage}
          </td>
        </tr>
      );
    }

    return data.map((item, rowIndex) => {
      const rowKey = getRowKey ? getRowKey(item, rowIndex) : (item.id ?? rowIndex);
      const handleActivate = onRowClick
        ? () => onRowClick(item, rowIndex)
        : undefined;

      return (
        <tr
          key={rowKey}
          className={onRowClick ? 'reusable-table-row-clickable' : undefined}
          onClick={handleActivate}
          onKeyDown={
            handleActivate
              ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleActivate();
                  }
                }
              : undefined
          }
          tabIndex={onRowClick ? 0 : undefined}
          role={onRowClick ? 'button' : undefined}
        >
          {columns.map((col, colIndex) => (
            <td key={`${rowKey}-${col.key || colIndex}`} className={col.className}>
              {col.render ? col.render(item, rowIndex) : item[col.key]}
            </td>
          ))}
        </tr>
      );
    });
  }, [data, columns, isLoading, loadingMode, skeletonRows, emptyMessage, onRowClick, getRowKey]);

  const hasData = data && data.length > 0;

  return (
    <div className={`reusable-table-container ${isLoading && hasData ? 'reusable-table-refreshing' : ''}`}>
      {/* Background loading progress bar */}
      <div className={`reusable-table-progress-bar ${isLoading ? 'visible' : ''}`} />
      
      <div className="reusable-table-responsive">
        <table className="reusable-table">
          <thead>
            <tr>
              {tableHeaders}
            </tr>
          </thead>
          <tbody className={isLoading && hasData ? 'reusable-table-body-searching' : ''}>
            {tableBody}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Table;
